import React, { useState, useEffect, useCallback } from "react";
import { marketerGroups as api, ref as refApi } from "../services/api";
import toast from "react-hot-toast";
import {
  FiPlus, FiTrash2, FiEdit2, FiUsers, FiSearch,
  FiX, FiCheck, FiUserPlus, FiUserMinus, FiChevronLeft
} from "react-icons/fi";

const COLORS = ["#8b5cf6","#10b981","#f59e0b","#ef4444","#3b82f6","#ec4899","#06b6d4","#f97316"];

export default function MarketerGroupsPage() {
  const [groups, setGroups]       = useState([]);
  const [selected, setSelected]   = useState(null); // group object
  const [members, setMembers]     = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showAdd, setShowAdd]     = useState(false);
  const [newName, setNewName]     = useState("");
  const [newDesc, setNewDesc]     = useState("");
  const [newColor, setNewColor]   = useState("#8b5cf6");
  const [editId, setEditId]       = useState(null);
  const [search, setSearch]       = useState("");
  const [addSearch, setAddSearch] = useState("");

  const loadGroups = useCallback(async () => {
    try {
      const res = await api.list();
      setGroups(res.data?.groups || []);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { loadGroups(); }, [loadGroups]);

  const loadMembers = useCallback(async (groupId) => {
    try {
      const res = await api.getMembers(groupId);
      setMembers(res.data?.members || []);
    } catch {}
  }, []);

  const loadClients = useCallback(async () => {
    try {
      const res = await refApi.getMarketers();
      setAllClients(res.data?.clients || res.data?.items || []);
    } catch {}
  }, []);

  useEffect(() => {
    if (selected) loadMembers(selected.id);
  }, [selected, loadMembers]);

  const selectGroup = (g) => {
    setSelected(g);
    setShowAdd(false);
  };

  const createGroup = async () => {
    if (!newName.trim()) return;
    try {
      await api.create({ name: newName.trim(), description: newDesc.trim() || null, color: newColor });
      toast.success("تم إنشاء المجموعة ✓");
      setNewName(""); setNewDesc(""); setShowCreate(false);
      loadGroups();
    } catch (e) {
      toast.error(e.response?.data?.error || "فشل الإنشاء");
    }
  };

  const updateGroup = async () => {
    if (!editId || !newName.trim()) return;
    try {
      await api.update(editId, { name: newName.trim(), description: newDesc.trim() || null, color: newColor });
      toast.success("تم التعديل ✓");
      setEditId(null); setNewName(""); setNewDesc(""); setShowCreate(false);
      loadGroups();
      if (selected?.id === editId) setSelected(prev => ({ ...prev, name: newName.trim(), color: newColor }));
    } catch (e) {
      toast.error(e.response?.data?.error || "فشل التعديل");
    }
  };

  const deleteGroup = async (id) => {
    if (!window.confirm("حذف المجموعة؟")) return;
    try {
      await api.delete(id);
      toast.success("تم الحذف ✓");
      if (selected?.id === id) { setSelected(null); setMembers([]); }
      loadGroups();
    } catch (e) {
      toast.error("فشل الحذف");
    }
  };

  const removeMember = async (clientId) => {
    if (!selected) return;
    try {
      await api.removeMember(selected.id, clientId);
      setMembers(prev => prev.filter(m => m.id !== clientId));
      loadGroups(); // refresh count
    } catch {}
  };

  const openAddDialog = () => {
    loadClients();
    setShowAdd(true);
    setAddSearch("");
  };

  const addClients = async (marketerIds) => {
    if (!selected || !marketerIds.length) return;
    try {
      await api.addMembers(selected.id, marketerIds);
      toast.success(`تمت إضافة ${marketerIds.length} مسوق ✓`);
      loadMembers(selected.id);
      loadGroups();
      setShowAdd(false);
    } catch (e) {
      toast.error("فشلت الإضافة");
    }
  };

  const startEdit = (g) => {
    setEditId(g.id);
    setNewName(g.name);
    setNewDesc(g.description || "");
    setNewColor(g.color || "#8b5cf6");
    setShowCreate(true);
  };

  const memberIds = new Set(members.map(m => m.id));
  const availableClients = allClients.filter(c =>
    !memberIds.has(c.id) &&
    (!addSearch || (c.name||"").includes(addSearch) || (c.phone||"").includes(addSearch))
  );

  return (
    <div style={{ height:"100vh", display:"flex", background:"#f8fafc" }}>
      {/* ── القائمة الجانبية: المجموعات ── */}
      <div style={{ width: selected ? 280 : "100%", maxWidth: 360, borderLeft:"1px solid #e2e8f0", display:"flex", flexDirection:"column", background:"#fff", flexShrink:0 }}>
        {/* Header */}
        <div style={{ padding:"16px 18px", borderBottom:"1px solid #e2e8f0", display:"flex", alignItems:"center", gap:10 }}>
          <FiUsers size={20} color="#8b5cf6"/>
          <span style={{ fontWeight:700, fontSize:17, color:"#1e293b", flex:1 }}>مجموعات المسوقين</span>
          <button onClick={() => { setShowCreate(true); setEditId(null); setNewName(""); setNewDesc(""); setNewColor("#8b5cf6"); }}
            style={{ background:"#8b5cf6", border:"none", borderRadius:8, padding:"6px 12px", color:"#fff", cursor:"pointer", fontSize:12, fontFamily:"inherit", display:"flex", alignItems:"center", gap:4 }}>
            <FiPlus size={14}/> جديد
          </button>
        </div>

        {/* Create/Edit form */}
        {showCreate && (
          <div style={{ padding:"12px 16px", borderBottom:"1px solid #e2e8f0", background:"#f0f9ff" }}>
            <input value={newName} onChange={e=>setNewName(e.target.value)} placeholder="اسم المجموعة..."
              style={{ width:"100%", padding:"8px 12px", border:"1px solid #93c5fd", borderRadius:8, fontSize:14, fontFamily:"inherit", boxSizing:"border-box", marginBottom:8 }}/>
            <input value={newDesc} onChange={e=>setNewDesc(e.target.value)} placeholder="وصف (اختياري)..."
              style={{ width:"100%", padding:"6px 12px", border:"1px solid #e2e8f0", borderRadius:8, fontSize:13, fontFamily:"inherit", boxSizing:"border-box", marginBottom:8 }}/>
            <div style={{ display:"flex", gap:6, marginBottom:10 }}>
              {COLORS.map(c => (
                <div key={c} onClick={() => setNewColor(c)}
                  style={{ width:24, height:24, borderRadius:"50%", background:c, cursor:"pointer", border: newColor===c?"3px solid #1e293b":"3px solid transparent" }}/>
              ))}
            </div>
            <div style={{ display:"flex", gap:8 }}>
              <button onClick={editId ? updateGroup : createGroup}
                style={{ flex:1, padding:"7px", background:"#8b5cf6", border:"none", borderRadius:8, color:"#fff", cursor:"pointer", fontSize:13, fontFamily:"inherit" }}>
                {editId ? "تعديل" : "إنشاء"}
              </button>
              <button onClick={() => { setShowCreate(false); setEditId(null); }}
                style={{ padding:"7px 14px", background:"#f1f5f9", border:"none", borderRadius:8, cursor:"pointer", fontSize:13, fontFamily:"inherit" }}>
                إلغاء
              </button>
            </div>
          </div>
        )}

        {/* Groups list */}
        <div style={{ flex:1, overflowY:"auto" }}>
          {loading ? (
            <div style={{ padding:30, textAlign:"center", color:"#94a3b8", fontSize:13 }}>جاري التحميل...</div>
          ) : groups.length === 0 ? (
            <div style={{ padding:40, textAlign:"center", color:"#94a3b8" }}>
              <FiUsers size={40} style={{ opacity:0.3, marginBottom:10 }}/>
              <div style={{ fontSize:14, marginBottom:6 }}>لا توجد مجموعات</div>
              <div style={{ fontSize:12 }}>أنشئ مجموعة لتصنيف المسوقين</div>
            </div>
          ) : groups.map(g => (
            <div key={g.id} onClick={() => selectGroup(g)}
              style={{
                padding:"12px 16px", cursor:"pointer", borderBottom:"1px solid #f8fafc",
                background: selected?.id === g.id ? "#eff6ff" : "transparent",
                borderRight: selected?.id === g.id ? `3px solid ${g.color||"#8b5cf6"}` : "3px solid transparent",
                display:"flex", alignItems:"center", gap:12, transition:"background 0.15s"
              }}>
              <div style={{
                width:38, height:38, borderRadius:"50%", flexShrink:0,
                background: g.color || "#8b5cf6",
                display:"flex", alignItems:"center", justifyContent:"center",
                color:"#fff", fontWeight:700, fontSize:14
              }}>
                <FiUsers size={16}/>
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontWeight:600, fontSize:14, color:"#1e293b" }}>{g.name}{g.bypass_wa_limits && <span title="مجموعة داخلية معفاة من حدود الواتس — الإرسال إليها مباشر بلا حد يومي ولا تأخير" style={{ fontSize:10, fontWeight:800, color:"#92400e", background:"#fef3c7", borderRadius:999, padding:"1px 7px", marginInlineStart:6 }}>🛡️ معفاة</span>}</div>{/* v3.4.418 */}
                <div style={{ fontSize:12, color:"#94a3b8" }}>{g.member_count} مسوق</div>
              </div>
              <div style={{ display:"flex", gap:4 }}>
                <button onClick={async e => { e.stopPropagation(); const v = !g.bypass_wa_limits; if (v && !window.confirm(`إعفاء «${g.name}» من حدود الواتس؟ الإرسال لأعضائها سيتم مباشرة بلا حد يومي ولا تأخير ولا استراحات (للمجموعات الداخلية فقط)`)) return; try { await api.update(g.id, { bypass_wa_limits: v }); setGroups(gs => gs.map(x => x.id === g.id ? { ...x, bypass_wa_limits: v } : x)); } catch { alert("فشل الحفظ"); } }}
                  title={g.bypass_wa_limits ? "إلغاء الإعفاء من حدود الواتس" : "إعفاء من حدود الواتس (إرسال داخلي مباشر)"} style={{ background:"none", border:"none", cursor:"pointer", color: g.bypass_wa_limits ? "#b45309" : "#cbd5e1", padding:4, fontSize:13 }}>🛡️</button>{/* v3.4.418 */}
                <button onClick={e => { e.stopPropagation(); startEdit(g); }}
                  style={{ background:"none", border:"none", cursor:"pointer", color:"#94a3b8", padding:4 }}>
                  <FiEdit2 size={13}/>
                </button>
                <button onClick={e => { e.stopPropagation(); deleteGroup(g.id); }}
                  style={{ background:"none", border:"none", cursor:"pointer", color:"#ef4444", padding:4 }}>
                  <FiTrash2 size={13}/>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── المحتوى: أعضاء المجموعة ── */}
      {selected && (
        <div style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden" }}>
          {/* Header */}
          <div style={{ padding:"14px 20px", borderBottom:"1px solid #e2e8f0", background:"#fff", display:"flex", alignItems:"center", gap:12 }}>
            <button onClick={() => setSelected(null)}
              style={{ background:"none", border:"none", cursor:"pointer", color:"#94a3b8", padding:4, display:"none" }}>
              <FiChevronLeft size={20}/>
            </button>
            <div style={{ width:36, height:36, borderRadius:"50%", background: selected.color||"#8b5cf6", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff" }}>
              <FiUsers size={16}/>
            </div>
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:700, fontSize:16, color:"#1e293b" }}>{selected.name}</div>
              <div style={{ fontSize:12, color:"#94a3b8" }}>{members.length} مسوق في المجموعة</div>
            </div>
            <button onClick={openAddDialog}
              style={{ background:"#8b5cf6", border:"none", borderRadius:8, padding:"7px 14px", color:"#fff", cursor:"pointer", fontSize:13, fontFamily:"inherit", display:"flex", alignItems:"center", gap:6 }}>
              <FiUserPlus size={14}/> إضافة مسوقين
            </button>
          </div>

          {/* Search */}
          <div style={{ padding:"10px 20px", background:"#fff", borderBottom:"1px solid #f1f5f9" }}>
            <div style={{ position:"relative" }}>
              <FiSearch size={14} style={{ position:"absolute", right:10, top:"50%", transform:"translateY(-50%)", color:"#94a3b8" }}/>
              <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="بحث في الأعضاء..."
                style={{ width:"100%", padding:"8px 32px 8px 12px", border:"1px solid #e2e8f0", borderRadius:8, fontSize:13, fontFamily:"inherit", outline:"none", boxSizing:"border-box" }}/>
            </div>
          </div>

          {/* Members list */}
          <div style={{ flex:1, overflowY:"auto", background:"#f8fafc" }}>
            {members.filter(m => !search || (m.name||"").includes(search) || (m.phone||"").includes(search)).map(m => (
              <div key={m.id} style={{
                padding:"11px 20px", display:"flex", alignItems:"center", gap:12,
                borderBottom:"1px solid #f1f5f9", background:"#fff"
              }}>
                <div style={{
                  width:36, height:36, borderRadius:"50%", background:"#e2e8f0",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  fontWeight:700, fontSize:14, color:"#64748b"
                }}>{(m.name||"?")[0]}</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:500, fontSize:14, color:"#1e293b" }}>{m.name}</div>
                  <div style={{ fontSize:12, color:"#94a3b8", direction:"ltr", textAlign:"right" }}>
                    {m.phone ? `+${m.phone}` : "بدون رقم"}
                  </div>
                </div>
                <button onClick={() => removeMember(m.id)}
                  style={{ background:"#fee2e2", border:"none", borderRadius:6, padding:"5px 10px", cursor:"pointer", color:"#dc2626", fontSize:12, fontFamily:"inherit", display:"flex", alignItems:"center", gap:4 }}>
                  <FiUserMinus size={12}/> حذف
                </button>
              </div>
            ))}
            {members.length === 0 && (
              <div style={{ padding:40, textAlign:"center", color:"#94a3b8" }}>
                <FiUsers size={36} style={{ opacity:0.3, marginBottom:8 }}/>
                <div style={{ fontSize:14 }}>لا يوجد أعضاء</div>
                <div style={{ fontSize:12 }}>اضغط "إضافة مسوقين" لإضافة أعضاء</div>
              </div>
            )}
          </div>

          {/* Add members dialog */}
          {showAdd && (
            <AddMembersDialog
              clients={availableClients}
              search={addSearch}
              onSearch={setAddSearch}
              onAdd={addClients}
              onClose={() => setShowAdd(false)}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ── Dialog لإضافة أعضاء ──
function AddMembersDialog({ clients, search, onSearch, onAdd, onClose }) {
  const [checked, setChecked] = useState(new Set());

  const toggle = (id) => setChecked(prev => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const toggleAll = () => {
    setChecked(checked.size === clients.length ? new Set() : new Set(clients.map(c => c.id)));
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.4)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ background:"#fff", borderRadius:16, width:"min(90vw,500px)", maxHeight:"80vh", display:"flex", flexDirection:"column", boxShadow:"0 20px 40px rgba(0,0,0,0.2)" }}>
        <div style={{ padding:"14px 18px", borderBottom:"1px solid #e2e8f0", display:"flex", alignItems:"center", gap:10 }}>
          <FiUserPlus size={18} color="#8b5cf6"/>
          <span style={{ fontWeight:700, fontSize:15, flex:1 }}>إضافة مسوقين للمجموعة</span>
          <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", color:"#94a3b8" }}><FiX size={18}/></button>
        </div>
        <div style={{ padding:"10px 18px", borderBottom:"1px solid #f1f5f9", display:"flex", gap:8, alignItems:"center" }}>
          <FiSearch size={14} color="#94a3b8"/>
          <input value={search} onChange={e=>onSearch(e.target.value)} placeholder="بحث..."
            style={{ flex:1, border:"none", outline:"none", fontSize:14, fontFamily:"inherit" }}/>
          <button onClick={toggleAll} style={{ fontSize:12, background:"#f1f5f9", border:"none", borderRadius:6, padding:"4px 10px", cursor:"pointer" }}>
            {checked.size === clients.length ? "إلغاء الكل" : "تحديد الكل"}
          </button>
          <span style={{ fontSize:12, background:"#dbeafe", color:"#1d4ed8", padding:"2px 8px", borderRadius:10, fontWeight:600 }}>{checked.size}</span>
        </div>
        <div style={{ flex:1, overflowY:"auto", maxHeight:400 }}>
          {clients.length === 0 ? (
            <div style={{ padding:30, textAlign:"center", color:"#94a3b8", fontSize:13 }}>لا يوجد مسوقين متاحين</div>
          ) : clients.map(c => (
            <label key={c.id} style={{
              display:"flex", alignItems:"center", gap:10, padding:"9px 18px",
              borderBottom:"1px solid #f8fafc", cursor:"pointer",
              background: checked.has(c.id) ? "#eff6ff" : "transparent"
            }}>
              <input type="checkbox" checked={checked.has(c.id)} onChange={() => toggle(c.id)}
                style={{ width:15, height:15, accentColor:"#8b5cf6" }}/>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:13, fontWeight:500 }}>{c.name}</div>
                <div style={{ fontSize:11, color:"#94a3b8" }}>{c.phone || "بدون رقم"}</div>
              </div>
            </label>
          ))}
        </div>
        <div style={{ padding:"12px 18px", borderTop:"1px solid #e2e8f0" }}>
          <button onClick={() => onAdd([...checked])} disabled={checked.size === 0}
            style={{
              width:"100%", padding:"10px", background: checked.size > 0 ? "#8b5cf6" : "#94a3b8",
              border:"none", borderRadius:8, color:"#fff", cursor:"pointer", fontSize:14, fontWeight:600, fontFamily:"inherit"
            }}>
            إضافة {checked.size} مسوق
          </button>
        </div>
      </div>
    </div>
  );
}
